import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { chatAPI } from '../../services/api';
import {
  ensureKeyPair,
  keyToBase64,
  encryptPayload,
  decryptPayload,
  generateConversationKey,
  encryptConversationKeyForUser,
  decryptConversationKeyShare,
  getConversationKey,
  setConversationKey,
  encryptFile,
  decryptFile,
} from '../../utils/chatCrypto';
import { registerChatPush } from '../../utils/pushNotifications';
import {
  Send,
  Paperclip,
  Mic,
  PlusCircle,
  Loader2,
  ShieldCheck,
  Volume2,
} from 'lucide-react';
import { getEchoInstance } from '../../lib/echo';

const Page = styled.div`
  display: flex;
  height: calc(100vh - 140px);
  padding: 1.5rem;
  gap: 1.5rem;
`;

const Sidebar = styled.div`
  width: 320px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ConversationItem = styled.button`
  width: 100%;
  padding: 0.85rem 1.25rem;
  border: none;
  background: ${(props) => (props.active ? '#f0f6ff' : 'transparent')};
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;

  &:hover {
    background: #f8fafc;
  }
`;

const ChatArea = styled.div`
  flex: 1;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
`;

const ChatHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  background: #f9fafb;
`;

const MessageBubble = styled.div`
  max-width: 70%;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: ${(props) => (props.isOwn ? '#2563eb' : '#fff')};
  color: ${(props) => (props.isOwn ? '#fff' : '#0f172a')};
  margin-left: ${(props) => (props.isOwn ? 'auto' : '0')};
  box-shadow: 0 10px 25px -18px rgba(15, 23, 42, 0.9);
`;

const Composer = styled.form`
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const ComposerInput = styled.textarea`
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.85rem;
  resize: none;
  min-height: 56px;
  max-height: 120px;
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: #eff2f9;
  color: #1d4ed8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  padding: 0.85rem 1.5rem;
  border-radius: 12px;
  border: none;
  background: #2563eb;
  color: #fff;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

+  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  width: min(600px, 95vw);
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Input = styled.input`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.75rem;
`;

const SearchResultList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`;

const SearchResultItem = styled.button`
  display: flex;
  justify-content: space-between;
  padding: 0.65rem 0.9rem;
  width: 100%;
  border: none;
  background: ${(props) => (props.selected ? '#e0edff' : 'transparent')};
  cursor: pointer;

  &:hover {
    background: #f5f7fb;
  }
`;

const AttachmentPreview = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

const AttachmentChip = styled.div`
  background: #e0edff;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  flex-direction: column;
  gap: 0.8rem;
`;

const ChatPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newChatVisible, setNewChatVisible] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [conversationTitle, setConversationTitle] = useState('');
  const [isGroup, setIsGroup] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [voiceRecorder, setVoiceRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [audioChunks, setAudioChunks] = useState([]);
  const [pushReady, setPushReady] = useState(false);

  const keyPair = useMemo(() => ensureKeyPair(), []);

  const loadKeyPairToServer = useCallback(async () => {
    const serverKey = await chatAPI.getKeyPair();
    if (!serverKey.data?.public_key) {
      await chatAPI.uploadKeyPair({
        public_key: keyToBase64(keyPair.publicKey),
      });
    }
  }, [keyPair]);

  const hydrateConversationKey = useCallback(
    (conversation) => {
      let key = getConversationKey(conversation.id);
      if (!key && conversation.key_shares?.length) {
        for (const share of conversation.key_shares) {
          const decrypted = decryptConversationKeyShare(share.encrypted_key, keyPair);
          if (decrypted) {
            setConversationKey(conversation.id, decrypted);
            key = decrypted;
            break;
          }
        }
      }
      return key;
    },
    [keyPair]
  );

  const fetchConversations = useCallback(async () => {
    const res = await chatAPI.listConversations();
    const fetched = res.data?.data || res.data || [];
    fetched.forEach(hydrateConversationKey);
    setConversations(fetched);
    if (!selectedConversation && fetched.length) {
      setSelectedConversation(fetched[0]);
    } else if (selectedConversation) {
      const updated = fetched.find((c) => c.id === selectedConversation.id);
      if (updated) {
        setSelectedConversation(updated);
      }
    }
  }, [hydrateConversationKey, selectedConversation]);

  const loadMessages = useCallback(
    async (conversation) => {
      if (!conversation) return;
      const res = await chatAPI.listMessages(conversation.id);
      const records = res.data?.data || res.data || [];
      const key = getConversationKey(conversation.id);
      const enriched = records
        .map((msg) => ({
          ...msg,
          decrypted: key ? decryptPayload(key, msg.encrypted_payload) : '[Encrypted message]',
        }))
        .reverse();
      setMessages(enriched);
    },
    []
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadKeyPairToServer();
      await fetchConversations();
      setLoading(false);
    })();
  }, [fetchConversations, loadKeyPairToServer]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation);
    }
  }, [selectedConversation, loadMessages]);

  useEffect(() => {
    if (!selectedConversation) return undefined;
    const echo = getEchoInstance();
    const channel = echo
      .private(`conversations.${selectedConversation.id}`)
      .listen('MessageSent', (event) => {
        const key = getConversationKey(selectedConversation.id);
        setMessages((prev) => [
          ...prev,
          {
            ...event,
            decrypted: key ? decryptPayload(key, event.encrypted_payload) : '[Encrypted message]',
          },
        ]);
      });
    return () => {
      channel.stopListening('MessageSent');
      echo.leave(`private-conversations.${selectedConversation.id}`);
    };
  }, [selectedConversation]);

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!selectedConversation) return;
    const key = getConversationKey(selectedConversation.id);
    if (!key) {
      alert('Missing encryption key for this conversation.');
      return;
    }
    if (!inputValue.trim() && attachments.length === 0) {
      return;
    }

    const encrypted = encryptPayload(key, inputValue.trim());
    const encryptedAttachments = [];

    for (const file of attachments) {
      const encryptedFile = await encryptFile(key, file);
      encryptedAttachments.push(encryptedFile);
    }

    await chatAPI.sendMessage(
      selectedConversation.id,
      {
        message_type: attachments.length ? 'file' : 'text',
        encrypted_payload: encrypted,
      },
      encryptedAttachments
    );
    setInputValue('');
    setAttachments([]);
    await loadMessages(selectedConversation);
    await fetchConversations();
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    setAttachments((prev) => [...prev, ...files]);
  };

  const startVoiceRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      alert('Voice recording not supported on this device.');
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    setVoiceRecorder(recorder);
    setRecording(true);
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'audio/webm' });
      const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
      setAttachments((prev) => [...prev, file]);
      setAudioChunks([]);
      setRecording(false);
    };
    recorder.start();
    setAudioChunks(chunks);
  };

  const stopVoiceRecording = () => {
    voiceRecorder?.stop();
    setVoiceRecorder(null);
    setRecording(false);
  };

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await chatAPI.searchUsers('');
      const users = res.data || [];
      setAllUsers(users);
      setFilteredUsers(users);
      setSearchResults(users);
    } catch (error) {
      console.error('Error fetching users:', error);
      setAllUsers([]);
      setFilteredUsers([]);
      setSearchResults([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSearchUsers = (value) => {
    setSearchQuery(value);
    if (!value || value.trim() === '') {
      setFilteredUsers(allUsers);
      setSearchResults(allUsers);
      return;
    }
    
    const query = value.toLowerCase().trim();
    const filtered = allUsers.filter(user => {
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').toLowerCase();
      return (
        fullName.includes(query) ||
        (user.first_name && user.first_name.toLowerCase().includes(query)) ||
        (user.last_name && user.last_name.toLowerCase().includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query)) ||
        (user.username && user.username.toLowerCase().includes(query)) ||
        (user.name && user.name.toLowerCase().includes(query)) // Fallback for backward compatibility
      );
    });
    setFilteredUsers(filtered);
    setSearchResults(filtered);
  };

  const handleOpenNewChat = () => {
    setNewChatVisible(true);
    setSearchQuery('');
    setSelectedUsers([]);
    setConversationTitle('');
    setIsGroup(false);
    if (allUsers.length === 0) {
      fetchAllUsers();
    } else {
      setFilteredUsers(allUsers);
      setSearchResults(allUsers);
    }
  };

  const toggleUserSelection = (userRecord) => {
    if (userRecord.id === user?.id) return;
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.id === userRecord.id);
      if (exists) {
        return prev.filter((u) => u.id !== userRecord.id);
      }
      return [...prev, userRecord];
    });
  };

  const createConversation = async (singleUser = null) => {
    const usersToUse = singleUser ? [singleUser] : selectedUsers;
    
    if (!usersToUse.length) {
      alert('Select at least one participant.');
      return;
    }
    
    const participants = usersToUse.map((u) => u.id);
    const type = usersToUse.length > 1 || isGroup ? 'group' : 'direct';
    const conversationKey = generateConversationKey();
    const keyShares = {};
    const allParticipants = [...new Set([...participants, user.id])];

    for (const participantId of allParticipants) {
      let targetPublicKey = null;
      if (participantId === user.id) {
        targetPublicKey = keyToBase64(keyPair.publicKey);
      } else {
        const match =
          usersToUse.find((u) => u.id === participantId && u.public_key) ||
          allUsers.find((u) => u.id === participantId && u.public_key) ||
          filteredUsers.find((u) => u.id === participantId && u.public_key);
        if (!match?.public_key) {
          alert('Selected user has not set up chat keys yet.');
          return;
        }
        targetPublicKey = match.public_key;
      }
      keyShares[participantId] = encryptConversationKeyForUser(conversationKey, targetPublicKey);
    }

    try {
      const response = await chatAPI.createConversation({
        type,
        title: type === 'group' ? conversationTitle || 'New Group' : null,
        member_ids: participants,
        key_shares: keyShares,
      });
      const created = response.data;
      setConversationKey(created.id, conversationKey);
      setSelectedConversation(created);
      setNewChatVisible(false);
      setSelectedUsers([]);
      setSearchQuery('');
      setConversationTitle('');
      setIsGroup(false);
      await fetchConversations();
    } catch (error) {
      console.error('Error creating conversation:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to create conversation. Please try again.';
      alert(errorMessage);
    }
  };

  const enablePush = async () => {
    await registerChatPush(chatAPI);
    setPushReady(true);
  };

  const downloadAttachment = async (attachment) => {
    if (!selectedConversation) return;
    const key = getConversationKey(selectedConversation.id);
    if (!key) {
      alert('Missing encryption key for this conversation.');
      return;
    }
    const response = await chatAPI.downloadAttachment(attachment.id);
    const decrypted = decryptFile(key, await response.data.arrayBuffer());
    if (!decrypted) {
      alert('Failed to decrypt attachment.');
      return;
    }
    const blob = new Blob([decrypted]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.file_name.replace(/\.enc$/, '');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <EmptyState>
        <Loader2 className="spin" />
        <p>Preparing encrypted chat…</p>
      </EmptyState>
    );
  }

  return (
    <Page>
      <Sidebar>
        <SidebarHeader>
          <div>
            <h3>Secure Chat</h3>
            <small style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <ShieldCheck size={16} /> E2EE active
            </small>
          </div>
          <IconButton onClick={handleOpenNewChat}>
            <PlusCircle size={20} />
          </IconButton>
        </SidebarHeader>
        <ConversationList>
          {conversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              active={selectedConversation?.id === conversation.id}
              onClick={() => setSelectedConversation(conversation)}
            >
              <strong>
                {conversation.title || 
                  (conversation.members
                    ?.filter((m) => m.user?.id !== user?.id)
                    .map((member) => {
                      const u = member.user;
                      if (!u) return null;
                      const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ');
                      return fullName || u.email || u.username;
                    })
                    .filter(Boolean)
                    .join(', ') || 'Direct chat')}
              </strong>
              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                {conversation.messages?.[0]?.created_at
                  ? new Date(conversation.messages[0].created_at).toLocaleString()
                  : 'No messages yet'}
              </div>
            </ConversationItem>
          ))}
        </ConversationList>
      </Sidebar>

      <ChatArea>
        {selectedConversation ? (
          <>
            <ChatHeader>
              <div>
                <strong>{selectedConversation.title || 'Direct chat'}</strong>
                <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                  {selectedConversation.members
                    ?.map((member) => {
                      const u = member.user;
                      if (!u) return null;
                      const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ');
                      return fullName || u.email || u.username;
                    })
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
              <PrimaryButton type="button" onClick={enablePush} disabled={pushReady}>
                Enable Push
              </PrimaryButton>
            </ChatHeader>

            <MessagesContainer>
              {messages.length === 0 ? (
                <EmptyState>
                  <p>No messages yet. Start the conversation!</p>
                </EmptyState>
              ) : (
                messages.map((message) => (
                  <MessageBubble key={message.id} isOwn={message.sender_id === user.id}>
                    <div>{message.decrypted}</div>
                    {message.attachments?.map((attachment) => (
                      <button
                        key={attachment.id}
                        style={{
                          marginTop: '0.5rem',
                          border: 'none',
                          background: '#fff2',
                          color: 'inherit',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={() => downloadAttachment(attachment)}
                      >
                        {attachment.file_name.replace(/\.enc$/, '')}
                      </button>
                    ))}
                  </MessageBubble>
                ))
              )}
            </MessagesContainer>

            <Composer onSubmit={handleSendMessage}>
              <input
                type="file"
                multiple
                accept="image/*,application/pdf,.doc,.docx,.xlsx,.pptx,audio/*,video/*"
                style={{ display: 'none' }}
                id="chat-file-input"
                onChange={handleFileSelect}
              />
              <AttachmentPreview>
                {attachments.map((file) => (
                  <AttachmentChip key={file.name}>
                    {file.name}
                    <button
                      type="button"
                      onClick={() =>
                        setAttachments((prev) => prev.filter((item) => item.name !== file.name))
                      }
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                    >
                      ×
                    </button>
                  </AttachmentChip>
                ))}
              </AttachmentPreview>
              <IconButton type="button" onClick={() => document.getElementById('chat-file-input').click()}>
                <Paperclip size={20} />
              </IconButton>
              <IconButton type="button" onClick={recording ? stopVoiceRecording : startVoiceRecording}>
                {recording ? <Volume2 size={20} /> : <Mic size={20} />}
              </IconButton>
              <ComposerInput
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type an encrypted message..."
              />
              <PrimaryButton type="submit" disabled={!inputValue.trim() && attachments.length === 0}>
                <Send size={18} /> Send
              </PrimaryButton>
            </Composer>
          </>
        ) : (
          <EmptyState>
            <p>Select a conversation or start a new one.</p>
          </EmptyState>
        )}
      </ChatArea>

      {newChatVisible && (
        <ModalBackdrop>
          <ModalCard>
            <h3>Create secure conversation</h3>
            <Field>
              <label>Search users ({filteredUsers.length} available)</label>
              <Input
                placeholder="Type a name or email to filter..."
                value={searchQuery}
                onChange={(e) => handleSearchUsers(e.target.value)}
              />
            </Field>
            <SearchResultList>
              {loadingUsers ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  Loading users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  {searchQuery ? 'No users found matching your search.' : 'No users available.'}
                </div>
              ) : (
                filteredUsers.map((result) => (
                  <SearchResultItem
                    key={result.id}
                    onClick={() => {
                      if (selectedUsers.length === 0 && !isGroup) {
                        // If no users selected and not a group, start conversation directly
                        createConversation(result);
                      } else {
                        // Otherwise, toggle selection
                        toggleUserSelection(result);
                      }
                    }}
                    selected={!!selectedUsers.find((u) => u.id === result.id)}
                  >
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <span>
                        {[result.first_name, result.last_name].filter(Boolean).join(' ') || 
                         result.name || 
                         result.email || 
                         result.username}
                      </span>
                      <small>{result.email}</small>
                    </div>
                    {selectedUsers.length === 0 && !isGroup && (
                      <PrimaryButton
                        type="button"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', marginLeft: '0.5rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          createConversation(result);
                        }}
                      >
                        Start Chat
                      </PrimaryButton>
                    )}
                  </SearchResultItem>
                ))
              )}
            </SearchResultList>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={isGroup}
                onChange={(e) => setIsGroup(e.target.checked)}
              />
              Group conversation
            </label>
            {isGroup && (
              <Field>
                <label>Group title</label>
                <Input value={conversationTitle} onChange={(e) => setConversationTitle(e.target.value)} />
              </Field>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              {selectedUsers.length > 0 && (
                <PrimaryButton type="button" onClick={() => createConversation()}>
                  {isGroup ? 'Create Group' : 'Start Conversation'}
                </PrimaryButton>
              )}
              <IconButton type="button" onClick={() => {
                setNewChatVisible(false);
                setSearchQuery('');
                setSelectedUsers([]);
                setConversationTitle('');
                setIsGroup(false);
              }}>
                Close
              </IconButton>
            </div>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Page>
  );
};

export default ChatPage;


